/// <amd-module name="DistributedTaskUI/Logs/IssuesComponent" />
import * as React from "react";

import { ExpandedLogsViewerComponent } from "DistributedTaskUI/Logs/ExpandedLogsViewerComponent";
import { Issue, IssueType } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsTelemetryHelper } from "DistributedTaskUI/Logs/LogsTelemetryHelper";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { Button, IButtonProps } from "VSSUI/Button";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { IVssPageContext } from "VSS/Platform/Context";
import { localeFormat } from "VSS/Core/Util/String";
import { Statuses } from "VSSUI/Status";
import { VssIconType } from "VSSUI/VssIcon";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/IssuesComponent";

interface IIssuesInfo {
    errors: string[];
    warnings: string[];
}

export interface IIssuesComponentProps {
    issues: Issue[] | undefined;
    headerText: string;
    pageContext?: IVssPageContext;
}

export interface ITaskListLogsControllerViewState {
    showPanel: boolean;
}

export class IssuesComponent extends React.Component<IIssuesComponentProps, ITaskListLogsControllerViewState> {

    constructor(props: IIssuesComponentProps) {
        super(props);
        this.state = { showPanel: false } as ITaskListLogsControllerViewState;
    }

    public render() {
        const issuesInfo = this._getIssuesInfo();
        const errorCounts = issuesInfo.errors && issuesInfo.errors.length;
        const warningCounts = issuesInfo.warnings && issuesInfo.warnings.length;
        // Render Issue component only when we have error or warning
        if (errorCounts > 0 || warningCounts > 0) {
            const messageBarType = errorCounts > 0 ? MessageBarSeverity.Error : MessageBarSeverity.Warning;
            return <div className="logs-phase-issue-container">
                <MessageBar
                    severity={messageBarType}
                    className="logs-error-message-bar-container" >
                    <Button
                        subtle={true}
                        className={"logs-error-message-action-button"}
                        onClick={this._showExpandedIssuesView}
                        ariaLabel={Resources.IssuesButtonAriaLabel}
                    >
                        {this._onRenderText()}
                    </Button>
                </MessageBar>
                {
                    this.state.showPanel &&
                    <ExpandedLogsViewerComponent
                        showPanel={this.state.showPanel}
                        headerTitle={this.props.headerText}
                        // remove headerIconProps in m143
                        headerIconProps={{ iconName: "StatusErrorFull", className: "state-failed", iconType: VssIconType.fabric }}
                        headerstatusProps={{ ...Statuses.Failed }}
                        fetchLogInProgress={false}
                        isLive={true}
                        followTail={false}
                        showLineNumber={false}
                        content={this._getContent()}
                        onCloseClick={this._onCloseClick}
                        pageContext={this.props.pageContext}
                    />
                }
            </div>;
        }

        return null;
    }

    private _onRenderText = (): JSX.Element | null => {
        const issues: Issue[] | undefined = this.props.issues;
        if (!issues) {
            return null;
        }

        // Get first non empty issue message
        let issueMessage: string = "";
        for (const issue of issues) {
            if (issue.message) {
                issueMessage = issue.message.trim();
                break;
            }
        }

        const issuesInfo = this._getIssuesInfo();
        const errorCounts = issuesInfo.errors && issuesInfo.errors.length;
        const warningCounts = issuesInfo.warnings && issuesInfo.warnings.length;
        const errorFormat = errorCounts > 1 ? Resources.Errors : Resources.Error;
        const warningFormat = warningCounts > 1 ? Resources.Warnings : Resources.Warning;

        return (
            <div className="logs-phase-issue-header-container" key="issue-container-key">
                <div className="logs-phase-issue-header">
                    <h3 className="logs-phase-issue-header-text">{this.props.headerText}</h3>
                    <div className="logs-phase-issue-header-separator">&middot;</div>
                    <h4 className="logs-phase-issue-info flex">
                        {errorCounts > 0 && <span>{localeFormat(errorFormat, errorCounts)}</span>}
                        {errorCounts > 0 && warningCounts > 0 && <span className="logs-phase-issue-separator">&middot;</span>}
                        {warningCounts > 0 && <span>{localeFormat(warningFormat, warningCounts)}</span>}
                    </h4>
                </div>
                <div className="logs-phase-issue-sub-status flex">
                    <div className="logs-phase-issue-message ellipsis-overflow">{issueMessage}</div>
                </div>
            </div>);
    }

    private _getIssuesInfo(): IIssuesInfo {
        if (!this._issuesInfo) {
            const issues: Issue[] = this.props.issues || [];
            const errors: string[] = [];
            const warnings: string[] = [];
            let errorsCount: number = 0;
            let warningsCount: number = 0;
            issues.forEach((issue) => {
                if (issue.message) {
                    if (issue.issueType === IssueType.Error) {
                        errorsCount++;
                        errors.push((localeFormat("##[Error {0}]\r\n{1} \r\n\r\n", errorsCount, issue.message.trim())));
                    } else {
                        warningsCount++;
                        warnings.push((localeFormat("##[Warning {0}]\r\n{1} \r\n\r\n", warningsCount, issue.message.trim())));
                    }
                }
            });

            this._issuesInfo = { errors: errors, warnings: warnings };
        }

        return this._issuesInfo;
    }

    private _getContent(): string {
        let content: string = "";
        if (this._issuesInfo) {
            const errors: string[] = this._issuesInfo.errors || [];
            const warnings: string[] = this._issuesInfo.warnings || [];

            errors.forEach((error) => {
                content += error;
            });

            warnings.forEach((warning) => {
                content += warning;
            });
        }

        return content;
    }

    private _showExpandedIssuesView = () => {
        this.setState({ showPanel: true } as ITaskListLogsControllerViewState);
        this._publishTelemetryToViewIssuesInPanel();
    }

    private _onCloseClick = () => {
        this.setState({ showPanel: false } as ITaskListLogsControllerViewState);
    }

    private _publishTelemetryToViewIssuesInPanel() {
        if (!this._issuesInfo) {
            return;
        }

        LogsTelemetryHelper.publishIssuesPanelTelemetry(this.props.pageContext, this._issuesInfo.errors, this._issuesInfo.warnings);
    }

    private _issuesInfo: IIssuesInfo | null = null;
}
